import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import { inputStyle } from "../OrderEntry/styles";
import { tokens, withAlpha } from "../../Common/designTokens";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: tokens.colors.bodyBackgroundColor,
  borderRadius: "8px",
  boxShadow: 24,
  p: 3,
};

const headerStyle = {
  pb: 2,
  borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
  mb: 2,
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  pt: 3,
  bgcolor: tokens.colors.defaultGridRowColor,
  "& > :not(style) + :not(style)": {
    ml: 2,
  },
};

const EditEndingWeightPortfolioRebalance = ({ open, onClose, rowData, onSubmit }) => {
  const [endingWeight, setEndingWeight] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (rowData && open) {
      setEndingWeight(
        rowData.endingSecurityWeightPercentage ?? ""
      );
      setValidationError("");
    }
  }, [rowData, open]);

  if (!rowData) return null;

  const endingWeightValue = rowData.endingSecurityWeightPercentage ?? "";
  const currentPrice = rowData.currentPrice || 0;
  const accruedInterest = rowData.accruedInterest || 0;

  const gridInputStyle = {
    ...inputStyle,
    "& .MuiInputBase-input::placeholder": {
      color: tokens.colors.fontColour85,
      opacity: 1,
    },
    "& .MuiFilledInput-input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 1000px ${tokens.colors.white} inset`,
      WebkitTextFillColor: tokens.colors.fontColour,
    },
  };

  const validatePercentage = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = Number(value);
    if (isNaN(num) || num <= 0 || num >= 100) {
      return "Please enter a valid percentage greater than 0 and less than 100.";
    }
    return "";
  };

  const handleValueChange = (newValue) => {
    if (newValue === "" || (typeof newValue === "number" && newValue >= 0)) {
      setEndingWeight(newValue === "" ? "" : Number(newValue));
      setValidationError(validatePercentage(newValue));
    }
  };

  const handleSubmit = () => {
    const error = validatePercentage(endingWeight);
    if (error) {
      setValidationError(error);
      return;
    }
    if (endingWeight === "") {
      setValidationError("Please enter a valid percentage");
      return;
    }
    onSubmit(Number(endingWeight));
    onClose();
  };

  const isValid = endingWeight !== "" && !validationError;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="edit-ending-weight-modal-title">
      <Box sx={modalStyle}>
        <Box sx={headerStyle}>
          <Typography id="edit-ending-weight-modal-title" variant="h6" fontWeight="bold" sx={{ color: tokens.colors.fontColour }}>
            Edit Ending Weight %
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            bgcolor: tokens.colors.defaultGridRowColor,
            my: 2,
            borderRadius: "4px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              CUSIP: {rowData.cusip}
            </Typography>
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: tokens.colors.bodyBorderColor50,
                height: 20,
              }}
            />
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              Side: <span style={{ color: rowData.side === "BUY" || rowData.side === "Buy" ? tokens.colors.buyFontColor : tokens.colors.sellFontColor }}>
                {rowData.side}
              </span>
            </Typography>
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: tokens.colors.bodyBorderColor50,
                height: 20,
              }}
            />
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              Price: {(currentPrice || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
            </Typography>
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: tokens.colors.bodyBorderColor50,
                height: 20,
              }}
            />
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              Acc Interest: {(accruedInterest || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50, mb: 3 }} />

        <Box sx={{ px: 1, pb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "120px 1fr 1fr",
              gap: 2,
              mb: 1,
              alignItems: "center",
            }}
          >
            <Box />
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour, textAlign: "center" }}>
              Original
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour, textAlign: "center" }}>
              Edited
            </Typography>
          </Box>

          <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50, mb: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "120px 1fr 1fr",
              gap: 2,
              mb: 2,
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour }}>
              Ending Weight %
            </Typography>

            <NumericFormat
              customInput={TextField}
              fullWidth
              value={endingWeightValue}
              variant="filled"
              placeholder="0"
              thousandSeparator=","
              allowNegative={false}
              decimalScale={4}
              disabled={true}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
            />

            <NumericFormat
              customInput={TextField}
              fullWidth
              value={endingWeight}
              variant="filled"
              placeholder="0"
              thousandSeparator=","
              allowNegative={false}
              decimalScale={4}
              disabled={false}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
              isAllowed={(v) => v.floatValue === undefined || (v.floatValue > 0 && v.floatValue < 100)}
              onValueChange={(v) => handleValueChange(v.floatValue)}
            />
          </Box>

          {validationError && (
            <Alert
              severity="error"
              sx={{
                backgroundColor: withAlpha(tokens.palette.danger, 0.133),
                color: tokens.palette.danger,
                border: `1px solid ${tokens.palette.danger}`,
                "& .MuiAlert-icon": {
                  color: tokens.palette.danger,
                },
              }}
            >
              {validationError}
            </Alert>
          )}
        </Box>

        <Box sx={{ mt: 3, mx: -3 }}>
          <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50 }} />
        </Box>

        <Box
          sx={{
            ...footerStyle,
            borderTop: "none",
            bgcolor: tokens.colors.bodyBackgroundColor,
            "& > :not(style) + :not(style)": {
              ml: 2,
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              backgroundColor: tokens.palette.primary,
              "&:hover": {
                backgroundColor: tokens.palette.primaryA80,
              },
              opacity: !isValid ? 0.5 : 1,
              cursor: !isValid ? "not-allowed" : "pointer",
              borderRadius: "8px",
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditEndingWeightPortfolioRebalance;
